import { AppPageProps } from '../types/app-types';
import React, { ReactNode, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { CurrentPathProvider } from '../contexts/useCurrentPath';
import { useSiteMetadataVersions } from '../queries/useSiteMetadataVersions';
import { getNormalizedVersionFromUrl } from '../utils/getNormalizedVersionFromUrl';
import { parseCurrentPath } from './appPage.utils';
import { CurrentVersionProvider } from '../contexts/useCurrentVersion';
import { DocOverview } from '../types/docs';
import { SECTION_OFFSET } from '../components/scroll/constants.utils';

/**
 * COMPUTATIONALLY INTENSE FUNCTION.
 * Be sure to memoize it.
 */
export function recursiveFind(doc: DocOverview, predicate: (doc: DocOverview) => boolean): DocOverview | null {
  if (predicate(doc)) return doc;
  for (const child of doc.children) {
    const found = recursiveFind(child, predicate);
    if (found) return found;
  }
  return null;
}

export default function AppPage({
  children,
  location,
  pageContext: { root, title, categoryVersions, id, permalink },
}: Omit<AppPageProps, 'children'> & {
  children: ReactNode;
}) {
  const { latestVersion } = useSiteMetadataVersions();
  const currentPath = parseCurrentPath(location.pathname);
  const version = getNormalizedVersionFromUrl(location.pathname, latestVersion);

  // React-scroll hack to make sure hashspy scrolls to correct position.
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash) return;

    const timeout = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY + SECTION_OFFSET;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 300); 

    return () => clearTimeout(timeout);
  }, []);

  return (
    <CurrentPathProvider currentPath={currentPath}>
      <CurrentVersionProvider currentVersion={version}>
        <Layout
          root={root}
          categoryVersions={categoryVersions}
          version={version}
          title={title}
          id={id}
          permalink={permalink}
        >
          {children}
        </Layout>
      </CurrentVersionProvider>
    </CurrentPathProvider>
  );
}
